import React, { KeyboardEventHandler, useCallback, useEffect, useState } from 'react';
import { Typography, TextField, Box, IconButton } from '@mui/material';
import { styled } from '@mui/system';
import { Backspace } from '@mui/icons-material';
import { RoundButton } from './round-button';
import { formatUSPhoneNumber } from '../utils/formatter';

const RoundButtonStyled = styled(RoundButton)({
    margin: 8
});

const TextFieldStyled = styled(TextField)({
    width: 320
});

const PhoneNumberBox = styled(Box)({
    margin: '8px 6px',
    textAlign: 'center',
    position: 'relative',
    '& button': {
        position: 'absolute',
        right: 0,
        top: -8,
        cursor: 'pointer'
    },
    '& h5': {
        overflowX: 'hidden'
    }
});

const NumpadContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'center'
});

const NumberContainer = styled(Box)({
    padding: '20px'
});

type NumberFieldVariant = 'none' | 'input' | 'text';

interface Props {
    onChar?: (char: string) => void;
    text?: string;
    onTextChange?: (text: string) => void;
    interceptKeyboard?: boolean;
    numberField?: NumberFieldVariant;
    formatPhoneNumber?: boolean;
    onEnterPress?: () => void;
}

export default function Numpad(props: Props) {
    const [text, setText] = useState(props.text || '');
    const [focus, setFocus] = useState(false);

    const updateText = useCallback((getText: (oldText: string) => string) => {
        setText(oldText => {
            const newText = getText(oldText);
            return newText;
        });
    }, []);

    useEffect(() => {
        if (props.onTextChange) {
            props.onTextChange(text);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, props.onTextChange]);

    const addChar = useCallback(
        (ch: string) => {
            if (props.onChar) {
                props.onChar(ch);
            }

            updateText(text => text + ch);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.onChar, updateText]
    );

    const erase = useCallback(() => updateText(text => text.slice(0, -1)), [updateText]);

    const handleKeyDown = useCallback(
        (key: string, code: string) => {
            if (/^[0-9*#]/.test(key)) {
                addChar(key);
                return true;
            }

            if (code === 'Backspace') {
                erase();
                return true;
            }

            if ((code === 'Enter' || code === 'NumpadEnter') && props.onEnterPress) {
                props.onEnterPress();
                return true;
            }

            return false;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [addChar, erase, props.onEnterPress]
    );

    const handleGlobalKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (!focus) {
                if (handleKeyDown(e.key, e.code)) e.preventDefault();
            }
        },
        [handleKeyDown, focus]
    );

    const handleInputKeyDown = useCallback<KeyboardEventHandler>(
        e => {
            if (focus) {
                handleKeyDown(e.key, e.code);
            }
        },
        [handleKeyDown, focus]
    );

    useEffect(() => {
        if (props.interceptKeyboard) {
            window.addEventListener('keydown', handleGlobalKeyDown);
        }

        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [props.interceptKeyboard, handleGlobalKeyDown]);

    const displayText = props.formatPhoneNumber ? formatUSPhoneNumber(text) : text;

    const renderBackspace = () =>
        text.length > 0 ? (
            <IconButton onClick={erase}>
                <Backspace color='primary' height={18} fontSize='small' />
            </IconButton>
        ) : (
            <></>
        );

    const renderNumberField = () => {
        if (props.numberField === 'input') {
            return (
                <TextFieldStyled
                    label='Type Number Here...'
                    variant='outlined'
                    value={displayText}
                    onKeyDown={handleInputKeyDown}
                    InputProps={{
                        endAdornment: renderBackspace()
                    }}
                    onFocus={() => setFocus(true)}
                    onBlur={() => setFocus(false)}
                />
            );
        }

        if (props.numberField === 'text') {
            return (
                <>
                    <PhoneNumberBox data-testid='numpad-display-div'>
                        <Typography variant='h5'>{displayText}&nbsp;</Typography>
                        {renderBackspace()}
                    </PhoneNumberBox>
                </>
            );
        }
    };

    const Button = useCallback(
        (btnProps: React.PropsWithChildren<{ char: string; noBackground?: boolean }>) => (
            <RoundButtonStyled onClick={() => addChar(btnProps.char)} noBackground={btnProps.noBackground} variant='lg'>
                <div>
                    <div>
                        <Typography variant='h4'>{btnProps.char}</Typography>
                    </div>
                    <div>
                        <Typography variant='body2'>{btnProps.children || '\u00A0'}</Typography>
                    </div>
                </div>
            </RoundButtonStyled>
        ),
        [addChar]
    );

    return (
        <div>
            <div>{renderNumberField()}</div>
            <NumberContainer>
                <NumpadContainer>
                    <Button char='1'></Button>
                    <Button char='2'>ABC</Button>
                    <Button char='3'>DEF</Button>
                </NumpadContainer>
                <NumpadContainer>
                    <Button char='4'>GHI</Button>
                    <Button char='5'>JKL</Button>
                    <Button char='6'>MNO</Button>
                </NumpadContainer>
                <NumpadContainer>
                    <Button char='7'>PQRS</Button>
                    <Button char='8'>TUV</Button>
                    <Button char='9'>WXYZ</Button>
                </NumpadContainer>
                <NumpadContainer>
                    <Button char='*' noBackground></Button>
                    <Button char='0'>+</Button>
                    <Button char='#' noBackground></Button>
                </NumpadContainer>
            </NumberContainer>
        </div>
    );
}
